import React, { useEffect, useState, useCallback } from "react";
import { Box, Text, Paper, ScrollArea, Loader, ActionIcon, Group, Center } from "@mantine/core";
import dayjs from "dayjs";
import { useIntersection } from "@mantine/hooks";
import { getAllCabTrip } from "../../../api/api";
import useStyles from "./Trips.styles";
import Heading from "../../../components/heading/Heading";
import { Car, Map2 } from "tabler-icons-react";
import { CabTrip as ICabTrip, Location } from "../../../@types";
import TripRouteMap from "./TripRouteMap";
import TruncatedLocation from "./TruncatedLocation";

interface CabTrip extends Omit<ICabTrip, 'source' | 'destination'> {
  source_geo_location: Location;
  destination_geo_location: Location;
  trip_locations?: {
    id: number;
    created_date: string;
    updated_date: string;
    created_by: string | null;
    updated_by: string | null;
    version: number;
    lat: number;
    lon: number;
  }[];
  note?: string;
  fare?: number;
}

interface GroupedTrips {
  [key: string]: CabTrip[];
}

const TripsScreen = () => {
  const { classes } = useStyles();
  const [trips, setTrips] = useState<CabTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTrip, setSelectedTrip] = useState<CabTrip | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Intersection observer for infinite scroll
  const [containerRef, observerEntry] = useIntersection({
    threshold: 0.5,
    root: null,
  });

  const fetchTrips = useCallback(async (pageNum: number) => {
    try {
      const response = await getAllCabTrip(pageNum);
      const newTrips = response.data.payload;
      if (pageNum === 0) {
        setTrips(newTrips);
      } else {
        setTrips(prev => [...prev, ...newTrips]);
      }
      setHasMore(newTrips.length === 20); // If we got less than requested size, we've reached the end
      setError(null);
    } catch (err) {
      setError("Failed to fetch trips");
      console.error("Error fetching trips:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchTrips(0);
  }, [fetchTrips]);

  useEffect(() => {
    if (observerEntry?.isIntersecting && hasMore && !loadingMore && !loading) {
      setLoadingMore(true);
      setPage(prev => {
        const nextPage = prev + 1;
        fetchTrips(nextPage);
        return nextPage;
      });
    }
  }, [observerEntry?.isIntersecting, hasMore, loadingMore, loading, fetchTrips]);

  const handleViewRoute = (trip: CabTrip) => {
    setSelectedTrip(trip);
    setMapOpen(true);
  };

  const groupTripsByDate = (trips: CabTrip[]): GroupedTrips => {
    return trips.reduce((groups: GroupedTrips, trip) => {
      const date = dayjs(trip.created_date).format("YYYY-MM-DD");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(trip);
      return groups;
    }, {});
  };

  const groupedTrips = groupTripsByDate(trips);
  const sortedDates = Object.keys(groupedTrips).sort((a, b) => dayjs(b).diff(dayjs(a)));

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <Loader size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.errorContainer}>
        <Text color="red">{error}</Text>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Heading title="Trips" />
      <ScrollArea className={classes.scrollArea}>
        {sortedDates.map((date) => (
          <Box key={date} mb="xl">
            <Text className={classes.dateHeader}>
              {dayjs(date).format("dddd, MMMM D, YYYY")}
            </Text>
            {groupedTrips[date].map((trip) => (
              <Paper 
                key={trip.id} 
                className={`${classes.tripCard} ${
                  trip.status === "COMPLETED" 
                    ? "completed" 
                    : trip.status === "CANCELLED" 
                    ? "cancelled"
                    : trip.status === "STARTED"
                    ? "started"
                    : trip.status === "WAITING_FOR_RIDE"
                    ? "waiting"
                    : "scheduled"
                }`} 
                p="md" 
                radius="sm"
                shadow="sm"
              >
                <Box className={`${classes.status} ${
                  trip.status === "COMPLETED" 
                    ? "completed" 
                    : trip.status === "CANCELLED" 
                    ? "cancelled"
                    : trip.status === "STARTED"
                    ? "started"
                    : trip.status === "WAITING_FOR_RIDE"
                    ? "waiting"
                    : "scheduled"
                }`}>
                  {trip.status === "WAITING_FOR_RIDE" ? "WAITING" : trip.status}
                </Box>
                <Box className={classes.tripHeader}>
                  <Box className={classes.tripIcon}>
                    <Car size={24} />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Group position="apart">
                      <Text weight={500} size="lg">
                        <TruncatedLocation text={trip.source_geo_location.name} /> → <TruncatedLocation text={trip.destination_geo_location.name} />
                      </Text>
                      {trip.status === "COMPLETED" && (
                        <ActionIcon
                          variant="light"
                          color="blue"
                          onClick={() => handleViewRoute(trip)}
                          title="View Route"
                        >
                          <Map2 size={18} />
                        </ActionIcon>
                      )}
                    </Group>
                    <Text size="sm" color="dimmed">
                      {dayjs(trip.scheduled_time).format("h:mm A")}
                      {trip.eta && ` (ETA: ${dayjs(trip.eta).format("h:mm A")})`}
                    </Text>
                  </Box>
                </Box>
                <Text size="sm">
                    <Text weight={500} component="span">Status:</Text> {trip.status}
                </Text>
                <Group mt="md" spacing="xl">
                  <Text size="sm">
                    <Text weight={500} component="span">Vehicle:</Text> {trip.cab_registration_number}
                  </Text>
                  <Text size="sm">
                    <Text weight={500} component="span">Driver:</Text> {trip.driver.first_name} {trip.driver.last_name}
                  </Text>
                  {trip.fare && trip.fare > 0 && (
                    <Text size="sm">
                      <Text weight={500} component="span">Fare:</Text> ₹{trip.fare}
                    </Text>
                  )}
                </Group>
                {trip.note && (
                  <Text size="sm" color="dimmed" mt="sm">
                    <Text weight={500} component="span">Note:</Text> {trip.note}
                  </Text>
                )}
              </Paper>
            ))}
          </Box>
        ))}
        {/* Loading indicator and intersection observer target */}
        <Box ref={containerRef} py="md">
          {loadingMore && (
            <Center>
              <Loader size="sm" />
            </Center>
          )}
        </Box>
      </ScrollArea>
      {selectedTrip && (
        <TripRouteMap
          opened={mapOpen}
          onClose={() => {
            setMapOpen(false);
            setSelectedTrip(null);
          }}
          source={selectedTrip.source_geo_location}
          destination={selectedTrip.destination_geo_location}
          tripLocations={selectedTrip.trip_locations || []}
        />
      )}
    </Box>
  );
};

export default TripsScreen;
